/**
 * Shared action bar for CRUD lists: filter toggle + CSV export + create
 * button, styled per the template's ListPageHeader. The export downloads
 * the currently filtered set (not just the visible page).
 */

import { useContext, useState, ReactNode } from 'react';
import {
  CreateButton,
  useDataProvider,
  useListContext,
  useNotify,
  useResourceContext,
  useTranslate,
} from 'react-admin';
import { Box, Button, useTheme } from '@mui/material';
import {
  FilterList as FilterListIcon,
  FileDownloadOutlined as ExportIcon,
} from '@mui/icons-material';
import { actionButtonSx, filterToggleSx } from './ListPageHeader';
import { FilterExpandContext } from './CollapsibleFilterPanel';
import { downloadCsv } from '@/utils/exportCsv';

interface CrudListActionsProps {
  showCreate?: boolean;
  showFilterToggle?: boolean;
  /** Columns to export; when set, an export button is rendered */
  exportColumns?: string[];
  children?: ReactNode;
}

const ExportButton = ({ exportColumns }: { exportColumns: string[] }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const resource = useResourceContext();
  const dataProvider = useDataProvider();
  const { filterValues, sort } = useListContext();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!resource) return;
    setExporting(true);
    try {
      const { data } = await dataProvider.getList(resource, {
        filter: filterValues,
        sort: sort ?? { field: 'id', order: 'DESC' },
        pagination: { page: 1, perPage: 10000 },
      });
      if (!data?.length) {
        notify('common.export_empty', { type: 'info' });
        return;
      }
      const columns = exportColumns.map((source) => ({
        source,
        label: translate(`resources.${resource}.fields.${source}`, { _: source }),
      }));
      downloadCsv(
        data,
        columns,
        `${translate(`resources.${resource}.name`, { smart_count: 2 })
          .toLowerCase()
          .replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`
      );
    } catch {
      notify('ra.notification.http_error', { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="outlined"
      sx={actionButtonSx}
      startIcon={<ExportIcon />}
      onClick={handleExport}
      disabled={exporting}
    >
      {translate('ra.action.export')}
    </Button>
  );
};

export const CrudListActions = ({
  showCreate = true,
  showFilterToggle = true,
  exportColumns,
  children,
}: CrudListActionsProps) => {
  const filterExpand = useContext(FilterExpandContext);
  const theme = useTheme();
  const translate = useTranslate();

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {showFilterToggle && (
        <Button
          onClick={() => filterExpand?.setExpanded(!filterExpand.expanded)}
          variant={filterExpand?.expanded ? 'contained' : 'outlined'}
          sx={filterToggleSx(theme, !!filterExpand?.expanded)}
          startIcon={<FilterListIcon />}
        >
          {translate('common.filters.label', { _: 'Filtros' })}
        </Button>
      )}
      {exportColumns && <ExportButton exportColumns={exportColumns} />}
      {children}
      {showCreate && <CreateButton variant="contained" sx={actionButtonSx} />}
    </Box>
  );
};

export default CrudListActions;
